import { Box, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import moment from 'moment';
import React from 'react';
import { OnlyFansMassDMMessageScheduleDocumentWithColor } from '../../../../hooks/useMessageUsersMassDmList';
import { OnlyFansSubscriber } from '../../../../hooks/useSubscriber';
import { usePagedList } from '../../../../hooks/usePagedList';
import ListViewPagination from '../../../common/ListViewPagination';
import MessageUsersAutoMassDmCalendarItemCard from './MessageUsersAutoMassDmCalendarItemCard';

const DateTitle = ({ date }: { date: Date }) => {
    return (
        <TableRow>
            <TableCell colSpan={2}>
                <Typography variant="h6" pl={1} pt={1} pb={1}>
                    {moment(date).format('MMMM D, YYYY')}
                </Typography>
            </TableCell>
        </TableRow>
    );
};

type Props = {
    subscriber: OnlyFansSubscriber;
    scheduledAutoMassDmMessage: OnlyFansMassDMMessageScheduleDocumentWithColor[];
    setSelectedDate: (date: moment.Moment) => void;
    isScheduledMessagesLoading: boolean;
    refetchMassDmsSchedule: () => void;
    refetchMassDms: () => void;
    setMassDmScheduleInactiveStatus: (massDmScheduleId: string, inactive: boolean) => Promise<{ success: boolean }>;
    isUpdatingScheduledMessageStatus: boolean;
};

const MessageUsersAutoMassDmCalendarListView = (props: Props) => {
    const {
        subscriber,
        scheduledAutoMassDmMessage,
        setSelectedDate,
        isScheduledMessagesLoading,
        refetchMassDms,
        refetchMassDmsSchedule,
        setMassDmScheduleInactiveStatus,
        isUpdatingScheduledMessageStatus,
    } = props;

    // Each item card is expensive to mount (nested table, tooltips, several context hooks),
    // so only one page of items is in the DOM at a time.
    const paged = usePagedList(scheduledAutoMassDmMessage);
    const visibleMessages = paged.pageItems;

    return (
        <Box
            ref={paged.topRef}
            sx={{
                display: 'block',
                overflowX: 'auto',
            }}
        >
            {!isScheduledMessagesLoading && scheduledAutoMassDmMessage && scheduledAutoMassDmMessage.length === 0 && (
                <Typography variant="body1" color="text.secondary" align="center" gutterBottom>
                    No Priority messages scheduled for this day.
                </Typography>
            )}
            {!isScheduledMessagesLoading && scheduledAutoMassDmMessage && scheduledAutoMassDmMessage.length > 0 && (
                <>
                    <Table
                        size="small"
                        sx={{
                            // No padding on the left and right of the table but only on the first and last cell
                            '& td': {
                                '&:first-of-type': {
                                    paddingLeft: '0px',
                                    paddingRight: '0px',
                                },
                                '&:last-child': {
                                    paddingRight: 1,
                                },
                                paddingLeft: 0.5,
                                paddingRight: 0.5,
                                paddingTop: 0,
                                paddingBottom: 0,
                            },
                            '& th': {
                                '&:first-of-type': {
                                    paddingLeft: '0px',
                                    paddingRight: '0px',
                                },
                                '&:last-child': {
                                    paddingRight: 1,
                                },
                                // Make header cells bold
                                fontWeight: 'bold',
                                paddingLeft: 0.5,
                                paddingRight: 0.5,
                            },
                        }}
                    >
                        <TableBody>
                            {visibleMessages.map((scheduledMessage, index) => (
                                <React.Fragment key={scheduledMessage._id}>
                                    {scheduledMessage.scheduleDate && index === 0 && (
                                        <DateTitle key={`date-${scheduledMessage._id}`} date={scheduledMessage.scheduleDate} />
                                    )}
                                    {scheduledMessage.scheduleDate &&
                                        index > 0 &&
                                        moment(visibleMessages[index - 1].scheduleDate).isSame(
                                            scheduledMessage.scheduleDate,
                                            'day',
                                        ) === false && (
                                            <DateTitle key={`date-${scheduledMessage._id}`} date={scheduledMessage.scheduleDate} />
                                        )}
                                    <TableRow>
                                        <TableCell colSpan={2}>
                                            <MessageUsersAutoMassDmCalendarItemCard
                                                subscriber={subscriber}
                                                scheduledAutoMassDmMessage={scheduledMessage}
                                                setSelectedDate={setSelectedDate}
                                                isFutureScheduledMessage={false}
                                                refetchMassDms={refetchMassDms}
                                                refetchMassDmsSchedule={refetchMassDmsSchedule}
                                                setMassDmScheduleInactiveStatus={setMassDmScheduleInactiveStatus}
                                                isUpdatingScheduledMessageStatus={isUpdatingScheduledMessageStatus}
                                            />
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                    <ListViewPagination {...paged} />
                </>
            )}
        </Box>
    );
};

export default MessageUsersAutoMassDmCalendarListView;
